import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { ImageOptimized, ImageOptimizedProps } from '../image/ImageOptimized';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { Item } from 'functions/src/types/firestore/Game/Collection/Item';
import { ShareLikedCount } from '../ShareLikedCount';
import { GradientTypography } from '../gradients/GradientTypography';
// import { isRare } from '../../util/catalogue-grid';
import { memo, useEffect, useState } from 'react';
import { CardSlot, CardSlotProps } from './CardSlot';
import Chip from '@mui/material/Chip';
import { useRouter } from 'src/hooks/useRouter';
import { CryptoChain } from 'src/web3/CryptoChain';

export type ItemCard2Props = Pick<
  Item<Date>,
  | 'imgUrl'
  | 'title'
  | 'contract'
  | 'likes'
  | 'id'
  | 'gameId'
  | 'rarity'
  | 'collectionId'
> & {
  children?: JSX.Element | JSX.Element[];
  gameTitle?: string;
  gameId?: string;
  isLootbox?: boolean;
} & Pick<CardSlotProps, 'onMouseEnter' | 'onMouseLeave' | 'onClick'> &
  Pick<ImageOptimizedProps, 'root'>;

export const ItemCard2 = memo(function ItemCard2Unmemoized(
  props: ItemCard2Props,
) {
  const {
    id,
    imgUrl,
    likes,
    rarity,
    title,
    gameTitle,
    gameId,
    children,
    isLootbox = false,
    contract,
    root,
    collectionId,
    onClick: onClickFromProps,
    onMouseEnter,
    onMouseLeave,
  } = props;
  const theme = useTheme();
  const { push } = useRouter();
  const [detailsAbsoluteUrl, setDetailsAbsoluteUrl] = useState('');
  const cryptoChain = CryptoChain.from(contract.chainId);
  const detailsUrl = `/item/${cryptoChain.coin.toLowerCase()}/${
    contract.address
  }/${id}`;
  useEffect(() => {
    if (!window) {
      return;
    }
    setDetailsAbsoluteUrl(`${window.location.hostname}${detailsUrl}`);
  }, [detailsUrl]);
  const onClick =
    onClickFromProps ||
    (() => {
      return push(detailsUrl);
    });
  //TODO: Should we refactor this?
  const rarityFormatted = rarity
    ? rarity.substring(rarity.indexOf('-') + 1)
    : undefined;
  const itemPath = `Game/${gameId}/Collection/${collectionId}/Item/${id}`;
  return (
    <CardSlot
      shape={
        isLootbox ? 'LootBox' : contract.type === 'ERC721' ? 'Card' : 'Pack'
      }
      rarity={rarity}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <>
        {isLootbox && (
          <Chip
            label={`Loot Box`}
            variant={'gradientOutlined'}
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 10,
              '&:hover': { cursor: 'pointer' },
            }}
          />
        )}
      </>
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
        <Box
          onClick={onClick}
          sx={{
            background: theme.palette.rarityBackground[`${rarity}`],
            border: '1px solid rgba(255,255,255,1)',
            borderRadius: 1,
            fontSize: 0,
          }}
        >
          <ImageOptimized
            src={imgUrl || ''}
            height={248}
            width={246}
            alt={title}
            style={{ borderRadius: '4px', backgroundColor: '#00294d' }}
            root={root}
            layout="fixed"
          />
        </Box>
      </Box>
      <CardContent>
        <Box
          onClick={onClick}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: 'white',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
              zIndex: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                textTransform: 'uppercase',
                mr: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {`${title} `}
              {!isLootbox && `#${contract?.itemId}`}
            </Typography>
            {/* TODO: Revisit data structures to facilitate packs {isPack && <PackTooltip />} */}
          </Box>
          {!!rarity && (
            <GradientTypography
              variant="subtitle1"
              gradientColor={`rarity.${rarity}`}
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: 1.43,
                letterSpacing: 0.17,
                textTransform: 'uppercase',
              }}
            >
              {rarityFormatted}
            </GradientTypography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: 'white',
            zIndex: 100,
          }}
        >
          <>
            {gameTitle ? (
              <Typography
                sx={{ color: theme.palette.text.secondary }}
                variant="body2"
              >
                {gameTitle?.charAt(0).toUpperCase() + gameTitle?.slice(1)}
              </Typography>
            ) : null}
          </>
          <ShareLikedCount
            shareUrl={detailsAbsoluteUrl}
            path={itemPath}
            likesOriginal={likes}
            guardTitle={'Like This item?'}
            likeButtonSx={{
              width: '20px',
              height: '20px',
              my: '-8px',
              ml: '-4px',
            }}
            likeCountProps={{
              variant: 'body2',
            }}
            shareButtonSx={{
              width: '18px',
              height: '18px',
              my: '-5px',
              mx: '-8px',
            }}
          />
        </Box>
      </CardContent>
      {children}
    </CardSlot>
  );
});
